import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../../Utils/toastContext";
import { debounce } from "lodash";
import { Line } from "react-chartjs-2";

const UnAllocatedStockPopup = ({ onClose, currentCustomerId }) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("10Y");
  const [isLoadingGraph, setIsLoadingGraph] = useState(false);
  const [rawData, setrawData] = useState([]);
  const [stockPrices, setStockprices] = useState([]);
  const [combineIndex, setCombineIndex] = useState([]);

  const labelsLine =
    rawData.length > 0
      ? rawData.map((item) => {
          const date = new Date(item.date); // Parse the date
          return date; // Return Date object (Chart.js can parse Date objects)
        })
      : [];
  const optionsLine = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        backgroundColor: "#3047EC",
        borderColor: "#4A90E2",
        borderWidth: 1,
        titleColor: "#AEC8FF",
        bodyColor: "white",
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            const timestamp = tooltipItems[0].parsed.x; // <-- reliable timestamp
            const date = new Date(timestamp);

            if (selectedFilter === "1M" || selectedFilter === "3M") {
              const day = date.getDate().toString().padStart(2, "0");
              const month = date.toLocaleDateString("en-US", {
                month: "short",
              });
              const year = date.getFullYear();
              return `${day} ${month} ${year}`; // Example: 09 Feb 2025
            } else {
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const year = date.getFullYear();
              return `${month}/${year}`; // Example: 02/2025
            }
          },

          label: (tooltipItem) => {
            const index = tooltipItem.dataIndex;
            const value = `$${tooltipItem.raw
              .toFixed(2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
            const isUpdated = rawData[index]?.version_changed;

            return isUpdated ? `${value} (SAA updated)` : value;
          },
        },
      },
      legend: { display: false },
    },
    scales: {
      x: {
        type: "time", // Ensure the x-axis uses time scale
        time: {
          unit:
            selectedFilter === "1M" || selectedFilter === "3M"
              ? "day"
              : "month",
          tooltipFormat:
            selectedFilter === "1M" || selectedFilter === "3M"
              ? "MMM d (EEE)"
              : "MMM yyyy",
        },
        grid: { drawOnChartArea: false, drawTicks: false },
        ticks: {
          display: true,
          color: "black",

          callback: function (value) {
            const date = new Date(value);

            if (selectedFilter === "1M" || selectedFilter === "3M") {
              const day = date.getDate().toString().padStart(2, "0");
              const month = date.toLocaleDateString("en-US", {
                month: "short",
              });

              return [day, month];
            } else {
              const month = date.toLocaleDateString("en-US", {
                month: "short",
              }); // e.g., Jan
              const year = date.getFullYear();
              return [month, year];
            }
          },
          font: {
            size: 12, // Adjust size to fit the labels properly
          },
          maxRotation: 0,
          minRotation: 0,
        },
        border: { color: "black" },
        afterFit: function (scale) {
          scale.height = 60; // Adjust the height to ensure space for two lines
        },
      },
      y: {
        grid: {
          drawOnChartArea: true, // Show grid lines on the chart area
          drawTicks: false, // Optionally hide ticks on grid lines
          color: "rgba(0, 0, 0, 0.1)", // Set the color of the grid lines (light gray)
          lineWidth: 1, // Set the line width of the grid lines
        },
        ticks: {
          callback: (value) => `${value.toFixed(2)}`,
          color: "black",
          padding: 10, // Add spacing for Y-axis labels
        },
        beginAtZero: false, // Optional: prevent starting at zero
        min: 0, // Adjust this value to add more space above the border
        offset: true, // Add extra space between the border and the graph
        border: { display: false },
      },
    },
  };
  function formatDateTime(inputDateStr) {
    const date = new Date(inputDateStr);

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
    const yyyy = date.getFullYear();

    const HH = String(date.getHours()).padStart(2, "0");
    const MM = String(date.getMinutes()).padStart(2, "0");
    const SS = String(date.getSeconds()).padStart(2, "0");

    return `${dd}/${mm}/${yyyy} | ${HH}:${MM}:${SS}`;
  }
  const dataLine = {
    labels: labelsLine,
    datasets: [
      {
        label: "Price",
        data: stockPrices,
        borderColor: "#3047EC",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return null; // Wait until the chart is fully rendered
          }

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          gradient.addColorStop(0, "rgba(74, 144, 226, 0.1)"); // Light blue at the top
          gradient.addColorStop(1, "white"); // White at the bottom

          return gradient;
        },
        tension: 0.4,
        fill: true,
        pointRadius: stockPrices.map((_, i) =>
          rawData[i].version_changed ? 5 : 0
        ),
        pointHoverRadius: stockPrices.map((_, i) =>
          rawData[i].version_changed ? 7 : 0
        ),
        pointBackgroundColor: rawData.map((item) =>
          item.version_changed ? "#3047EC" : "transparent"
        ),
        pointBorderColor: rawData.map((item) =>
          item.version_changed ? "#3047EC" : "transparent"
        ),
        pointBorderWidth: rawData.map((item) => (item.version_changed ? 3 : 0)),
      },
      {
        label: "Combine indices",
        data: combineIndex,
        borderColor: "#DC4646",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return null; // Wait until the chart is fully rendered
          }

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          gradient.addColorStop(0, "rgba(220, 70, 70, 0.3)"); // Light red (same as border, low opacity)
          gradient.addColorStop(1, "rgba(220, 70, 70, 0)");

          return gradient;
        },
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };
  const GetUnallocatedList = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("jwtToken");
    let apiUrl = `${BASE_URL}/v1/external-stocks/submissions/${currentCustomerId}?status=APPROVED`;

    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));

        throw new Error(errorData.message || "Something went wrong");
      }
      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Network error, please try again later");
    }
  };
  const getAllUserUnallocatedAccounts = async () => {
    try {
      const response = await GetUnallocatedList();

      if (response.status == 200) {
        setStockData(response.data.submissions);
      } else {
        if (response.status === 400) {
          showToast(response.message, "error");
          return;
        }
        setStockData([]);
      }
    } catch (err) {
      setStockData([]);
      showToast(err.message, "error");
    } finally {
      setTimeout(function () {
        setIsLoading(false);
      }, 100);
    }
  };
  useEffect(() => {
    getAllUserUnallocatedAccounts();
  }, [currentCustomerId]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {!isLoading && (
        <div className="bg-white md:max-w-4xl w-[90%] p-6 max-h-[90vh]  overflow-y-auto rounded-lg shadow-lg relative">
          {/* Header */}
          <div className="flex justify-between items-center pb-4 mb-4">
            <h2 className="text-xl font-semibold text-center w-full">
              Account Details
            </h2>
            <button
              onClick={onClose}
              className="absolute right-6 text-gray-500 hover:text-black text-lg"
            >
              <img
                src="/add.svg"
                alt="Close"
                className="w-8 h-8 md:w-10 md:h-10"
              />
            </button>
          </div>
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="min-w-full text-sm text-left border border-gray-200 dark:border-gray-700">
              <thead className="bg-[#ECFAFF] text-[#005A9C] text-xs md:text-sm">
                <tr>
                  <th className="px-4 py-2 border border-gray-300 dark:border-gray-600">
                    Code
                  </th>
                  <th className="px-4 py-2 border border-gray-300 dark:border-gray-600">
                    Stock Name
                  </th>

                  <th className="px-4 py-2 border border-gray-300 dark:border-gray-600">
                    Quantity
                  </th>
                  <th className="px-4 py-2 border border-gray-300 dark:border-gray-600">
                    Purchased Date
                  </th>
                </tr>
              </thead>

              {isLoading ? (
                <tbody>
                  {[...Array(5)].map((_, rowIndex) => (
                    <tr key={rowIndex} className="animate-pulse">
                      {[...Array(2)].map((_, colIndex) => (
                        <td key={colIndex} className="px-4 py-3">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody className="text-black dark:text-gray-200">
                  {stockData.length > 0 ? (
                    stockData.map((row, index) => (
                      <tr
                        key={index}
                        className="hover:bg-[#f0faff] dark:hover:bg-[#2a2a2a] transition-colors"
                      >
                        <td className="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-[#ECFAFF]">
                          {row.stock_asx}
                        </td>
                        <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">
                          {row.stock_name}
                        </td>
                        <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">
                          {row.quantity
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        </td>
                        <td className="px-4 py-2 border border-gray-200 dark:border-gray-700">
                          {formatDateTime(row.purchase_date)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="2"
                        className="text-center text-sm py-6 text-gray-500 dark:text-gray-400"
                      >
                        No results found
                      </td>
                    </tr>
                  )}
                </tbody>
              )}
            </table>
          </div>
          <div className="py-6 px-4 bg-white">
            <div className="mb-4 flex flex-wrap justify-between items-center">
              <span>
                <h3 className="text-lg font-semibold text-gray-800 w-full md:w-auto">
                  Historical Performance
                </h3>
              </span>{" "}
            </div>
            <div className=" flex w-full justify-between flex-wrap">
              <div className="pr-4 flex-wrap items-center">
                <div className="flex">
                  {["10Y", "5Y", "1Y", "3M", "1M"].map((filter, index) => (
                    <button
                      key={filter}
                      className={`px-3 py-1 shadow-md ${
                        filter === "1M" ? "rounded-r-xl" : ""
                      }
            ${filter === "10Y" ? "rounded-l-xl" : ""}
             
               ${
                 selectedFilter === filter
                   ? "bg-black text-white"
                   : "bg-white text-black"
               }`}
                      onClick={() => setSelectedFilter(filter)}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>
              {/* Flex container for image and performance text */}
              <div className="flex flex-row mt-8 md:mt-0   md:flex-row items-start ml-auto space-x-0 space-y-0">
                <div className="flex items-center">
                  <img src="/blue-taa.svg" alt="taa" className="mr-1" />
                  <span className="text-xs text-black">Total Price</span>
                </div>
                <div className="flex items-center pl-2">
                  <img
                    src="/red-saa.svg"
                    alt="income-orange"
                    className="mr-1"
                  />
                  <span className="text-xs text-black">Combined Indices</span>
                </div>
              </div>
            </div>
            {isLoadingGraph ? (
              <div className="shimmer-container-list mb-4 ">
                {/* Creating 5 rows of shimmer effect */}
                {[...Array(2)].map((_, rowIndex) => (
                  <div key={rowIndex} className="shimmer-row-list">
                    {/* Creating 7 cells in each row for shimmer */}
                    {[...Array(2)].map((_, colIndex) => (
                      <div key={colIndex} className="shimmer-cell-list h-20">
                        <div className="shimmer-loader"></div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ width: "100%", height: "400px" }}>
                <Line data={dataLine} options={optionsLine} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnAllocatedStockPopup;
